import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { LoadersComponent } from 'src/app/layout/loaders/loaders.component';
import { ChoosePaymentMethodComponent } from 'src/app/shared/components/choose-payment-method/choose-payment-method.component';
import { GhostButtonComponent } from 'src/app/shared/components/ghost-button/ghost-button.component';
import { PeuTableComponent } from '../components/peu-table/peu-table.component';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PayWithRedirectResponse, PeuPayments, PeuPaymentStatus } from '../models/peu-payment.model';
import { finalize, Observable, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import { PeuPaymentService } from '../services/peu-payment.service';
import { ChoosenPaymentType } from 'src/app/shared/components/choose-payment-method/choosen-payment-type.model';
import { PaymentMethodType } from 'src/app/shared/models/payment/payment-dto.model';
import { HttpErrorResponse } from '@angular/common/http';
import { PEU_PAYMENT_CONFIRMATION, PEU_PAYMENT_PAGE, PEU_ROUTE } from '../_consts/peu-payments-route.const';

@Component({
  selector: 'app-peu-payment',
  standalone: true,
  imports: [
    CommonModule,
    PeuTableComponent,
    LoadersComponent,
    ChoosePaymentMethodComponent
  ],
  templateUrl: './peu-payment.component.html',
  styleUrl: './peu-payment.component.scss'
})
export class PeuPaymentComponent implements OnInit {
  content: any;
  splashEnabled: boolean = false;
  id: string;
  payment: PeuPayments;
  waitingForData: boolean;
  paymentStarted: boolean = false;
  timeInterval: Subscription;
  billID: string;
  private paymentPolling$: Subscription | null = null;
  private paymentSuccess$ = new Subject<void>();

  constructor(private translate: TranslateService, private route: ActivatedRoute, private router: Router, private toast: ToastrService, private service: PeuPaymentService) {
    this.id = this.route.snapshot.paramMap.get('id')

    this.translate.get('PEU_PAYMENTS').subscribe({
      next: (content: any) => (this.content = content)
    });
  }

  ngOnInit(): void {
    if (this.id) {
      this.getPayment(this.id).subscribe({
        next: (payment: PeuPayments) => (this.payment = payment),
        error: (error: { error: { detail: string }, status: number }) => {
          console.log(error)
          if(error?.status === 404) {
            throw error;
          } else {
            this.toast.error(error?.error?.detail);
          }
        }
      });
    }
  }

  getPayment(id: string): Observable<PeuPayments> {
    this.waitingForData = true;
    return this.service.getPayment(id).pipe(finalize(() => this.waitingForData = false));
  }

  onSubmit(event: ChoosenPaymentType) {
    switch (event.paymentType) {
      case PaymentMethodType.BLIK:
        this.proceedWithBlik(event.blikCode);
        break;
      case PaymentMethodType.E_CARD:
        this.proceedWithECard();
        break;
      case PaymentMethodType.PAYMENT_CARD:
        this.proceedWithPaymentCard();
        break;
      default:
        console.error('Nieobsługiwany typ płatności');
        break;
    }
  }

  private proceedWithECard() {
    this.service.createPayment$(this.id).subscribe({
      next: (result) => {
        if (result.paymentStatus === PeuPaymentStatus.EXCEEDED) {
          this.handlePaymentError(this.content.EXCEEDED.paymentInfo);
        } else {
          window.location.href = result.redirectUrl;
        }
      },
      error: (error) => {
        this.handlePaymentError(null, error);
      }
    });
  }

  proceedWithPaymentCard() {
    this.proceedWithECard();
  }

  private proceedWithBlik(blikCode: string) {
    this.splashEnabled = true;
    this.paymentStarted = true;

    this.service.createBlikPayment$(this.payment?.id, this.createBlikObject(blikCode))
      .subscribe({
        next: (result) => {
          this.billID = result.billId;
          this.poolBlikPaymentStatus();
        },
        error: (error) => {
          this.handlePaymentError(null, error);
        }
      });
  }

  private createBlikObject(code: string) {
    return {
      blikCode: code
    };
  }

  handlePaymentError(toastrMessage: string, error?: HttpErrorResponse) {
    this.splashEnabled = false;
    this.paymentStarted = false;
    toastrMessage ? this.toast.error(toastrMessage) : null;

    if (error.error?.errors?.length) {
      for (const e of error.error.errors) {
        e.userMessage ? this.toast.error(e.userMessage) : null;
        if (e.code === 'payment-already-started-or-accepted') {
          this.router.navigate(['/', e.path]);
        }
      }
    } else if (error?.error?.message?.length) {
      this.toast.error(error.error.message)
    } else if (error?.error?.detail) {
      this.toast.error(error?.error?.detail)
    }
  }

  private poolBlikPaymentStatus(): void {
    if (this.paymentPolling$) {
      return;
    }

    this.paymentPolling$ = timer(0, 3000)
      .pipe(
        switchMap(() =>  this.service.getPaymentStatus(this.billID)),
        takeUntil(this.paymentSuccess$),
        finalize(() => {
          this.paymentPolling$?.unsubscribe();
          this.paymentPolling$ = null;
        })
      )
      .subscribe({
        next: (data: PeuPayments) => this.handleBlikPaymentStatus(data),
        error: (error) => this.handlePaymentError(null, error)
      });
  }

  private handleBlikPaymentStatus(bill): boolean {
    if (bill.paymentStatus === PeuPaymentStatus.EXCEEDED) {
      this.handlePaymentError('Płatnośc nie powiodła się');
    } else if (bill.paymentStatus === PeuPaymentStatus.AUTHACCEPTED || bill.paymentStatus === PeuPaymentStatus.AUTHREJECTED) {
      this.splashEnabled = false;
      this.paymentStarted = false;
      this.paymentPolling$.unsubscribe();
      this.paymentSuccess$.complete();
      this.timeInterval ? this.timeInterval.unsubscribe() : null;
      this.router.navigate([PEU_ROUTE, PEU_PAYMENT_PAGE, PEU_PAYMENT_CONFIRMATION, bill.billId]);
    }
    return false;
  }

  ngOnDestroy(): void {
    if(this.paymentPolling$) {
      this.paymentPolling$.unsubscribe();
    }

    if(this.paymentSuccess$) {
      this.paymentSuccess$.complete();
    }
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilityAction(
    event: any
  ) {
    if (!document.hidden && this.paymentStarted) {
      this.poolBlikPaymentStatus();
    }
  }

  @HostListener('window:focus', ['$event'])
  focusAction(
    event: any
  ): void {
    if (this.paymentStarted) {
      this.poolBlikPaymentStatus();
    }
  }
}
